<template>
  <!-- メールアプリ内ブラウザ検知時の警告オーバーレイ -->
  <div v-if="showWarning" class="in-app-browser-overlay">
    <div class="in-app-browser-warning">
      <div class="warning-icon">&#9888;</div>
      <h2 class="warning-title">外部ブラウザで開いてください</h2>
      <p class="warning-message">{{ detection.appName }}のアプリ内ブラウザでは正常に動作しない場合があります。</p>
      <div class="warning-instruction">
        <p class="instruction-label">操作手順：</p>
        <p class="instruction-text">{{ detection.instruction }}</p>
      </div>
      <div class="warning-url-section">
        <p class="url-label">または、以下のURLをブラウザに貼り付けてください：</p>
        <div class="url-box" @click="copyUrl">
          <span class="url-text">{{ currentUrl }}</span>
          <span class="copy-hint">{{ copyMessage }}</span>
        </div>
      </div>
      <!-- 誤判定時のエスケープハッチ: ユーザーがブロックされないように「このまま続ける」オプションを提供 -->
      <button class="dismiss-button" @click="dismiss">理解した上で、このまま続ける</button>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, toNative } from 'vue-facing-decorator';
import { detectInAppBrowser, type InAppBrowserDetectionResult } from '@/utils/inAppBrowser';
import { useUserStore } from '@/stores/index';
import { useAnswerDateStore } from '@/stores/answer_date';

/**
 * メールアプリ内ブラウザを検知した場合に、
 * 外部ブラウザでの表示を促す警告オーバーレイを表示するコンポーネント。
 *
 * Androidのメールアプリからwebsurveyの回答URLを開いた際に、
 * メールソフト内の組み込みブラウザ（WebView）で開かれてしまう問題への対策。
 */
@Component({})
class InAppBrowserWarning extends Vue {
  /** メールアプリ内ブラウザの検知結果 */
  detection: InAppBrowserDetectionResult = {
    isInAppBrowser: false,
    appName: '',
    instruction: '',
  };

  /** 現在のURL（ユーザーにコピーさせる用） */
  currentUrl = '';

  /** コピーボタンのメッセージ */
  copyMessage = 'タップしてコピー';

  /** ユーザーが警告を閉じたかどうか */
  dismissed = false;

  /**
   * WebView警告の対象導線かどうかを判定する。
   * WebSurvey（メールからの回答）または回答リンク（auto_sign_in）経由のアクセス、
   * もしくは初回ランディングURL（/login-websurvey, /forms/:uuid）の場合のみ対象。
   * 通常ログインでの回答フローでは警告を表示しない（誤判定リスク軽減）。
   */
  get isTargetFlow(): boolean {
    const userStore = useUserStore();
    const answerDateStore = useAnswerDateStore();
    const path = window.location.pathname;
    // 初回ランディング時はストアのフラグが未設定の場合があるため、URLでもカバー
    const isLandingUrl = path.endsWith('/login-websurvey') || /\/forms\/[^/]+$/.test(path);
    return !!(userStore.is_websurvey || answerDateStore.is_auto_sign_in || isLandingUrl);
  }

  /** 警告オーバーレイの表示判定（対象導線 かつ 検知 かつ 未ディスミス） */
  get showWarning(): boolean {
    return this.isTargetFlow && this.detection.isInAppBrowser && !this.dismissed;
  }

  created(): void {
    // 対象導線の場合のみ検知処理を実行（他導線でのUA判定コストを削減）
    if (this.isTargetFlow) {
      this.detection = detectInAppBrowser();
    }
    this.currentUrl = window.location.href;
  }

  /** 警告を閉じてそのまま続ける（誤判定時のエスケープハッチ） */
  dismiss(): void {
    this.dismissed = true;
  }

  /**
   * URLをクリップボードにコピーする。
   * WebView環境ではClipboard APIが利用不可の場合があるため、
   * 事前にAPIの存在を確認し、利用不可時は手動コピーを案内する。
   */
  async copyUrl(): Promise<void> {
    // Clipboard APIが利用不可の場合（一部WebViewではAPIが存在しない）
    if (!navigator.clipboard?.writeText) {
      this.copyMessage = 'URLを長押ししてコピーしてください';
      setTimeout(() => {
        this.copyMessage = 'タップしてコピー';
      }, 3000);
      return;
    }

    try {
      await navigator.clipboard.writeText(this.currentUrl);
      this.copyMessage = 'コピーしました';
      setTimeout(() => {
        this.copyMessage = 'タップしてコピー';
      }, 2000);
    } catch {
      // Clipboard APIは存在するが権限エラー等で失敗した場合
      this.copyMessage = 'URLを長押ししてコピーしてください';
      setTimeout(() => {
        this.copyMessage = 'タップしてコピー';
      }, 3000);
    }
  }
}
export default toNative(InAppBrowserWarning);
</script>

<style scoped>
.in-app-browser-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  /* Loadingコンポーネント（z-index: 10000）より上に表示する */
  z-index: 15000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.in-app-browser-warning {
  background-color: #ffffff;
  border-radius: 12px;
  padding: 30px 24px;
  max-width: 400px;
  width: 100%;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.warning-icon {
  font-size: 48px;
  color: #f0ad4e;
  margin-bottom: 12px;
}

.warning-title {
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-bottom: 12px;
}

.warning-message {
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.6;
}

.warning-instruction {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  text-align: left;
}

.instruction-label {
  font-size: 13px;
  font-weight: bold;
  color: #333;
  margin-bottom: 6px;
}

.instruction-text {
  font-size: 14px;
  color: #555;
  line-height: 1.6;
  margin-bottom: 0;
}

.warning-url-section {
  text-align: left;
}

.url-label {
  font-size: 12px;
  color: #888;
  margin-bottom: 8px;
}

.url-box {
  background-color: #e9ecef;
  border-radius: 6px;
  padding: 10px 12px;
  cursor: pointer;
  word-break: break-all;
  position: relative;
}

.url-text {
  font-size: 12px;
  color: #333;
  line-height: 1.4;
}

.copy-hint {
  display: block;
  font-size: 11px;
  color: #007bff;
  margin-top: 6px;
  text-align: center;
}

.dismiss-button {
  margin-top: 16px;
  background: none;
  border: none;
  color: #999;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
  padding: 4px 8px;
}
</style>
